import {
  ChakraFlex,
  ChakraVStack,
  FAIconName,
  FireflyButton,
  Text,
} from '@equip.health/ui';
import { GetDischargePlanResponse } from '~/store/services/clinical-data/dischargePlan';
import AftercareSectionCard from './section-cards/AftercareSectionCard';
import { getDischargeStatuses } from '../util/dischargePlanUtils';
import { useCallback } from 'react';
import {
  AFTERCARE_AND_REFERRAL_FIELDS,
  AftercareAndReferralsSectionField,
} from './section-cards';
import { Section } from '~/components/common/Section';
import SectionHeader from '~/components/common/SectionHeader';

interface AftercareRecommendationsSectionProps {
  dischargePlanData?: GetDischargePlanResponse;
  onSelectField: (fieldName: AftercareAndReferralsSectionField | null) => void;
}

const AftercareRecommendationsSection = ({
  dischargePlanData,
  onSelectField,
}: AftercareRecommendationsSectionProps) => {
  const AFTERCARE_FIELDS = AFTERCARE_AND_REFERRAL_FIELDS.filter(
    (field) => field.key !== 'referralsAndFollowUpAppointments',
  );

  const handleSelectField = useCallback(
    (fieldName: AftercareAndReferralsSectionField) => {
      const fieldToSelect =
        AFTERCARE_FIELDS.find((field) => field.key === fieldName.key) ?? null;
      onSelectField(fieldToSelect);
    },
    [AFTERCARE_FIELDS, onSelectField],
  );

  const { isSigned } = getDischargeStatuses(dischargePlanData?.status);

  const populatedFields =
    AFTERCARE_FIELDS.filter((field) => dischargePlanData?.[field.key]) ?? [];

  const emptyFields =
    AFTERCARE_FIELDS.filter((field) => !dischargePlanData?.[field.key]) ?? [];

  return (
    <Section id="aftercare-recommendations-section">
      <SectionHeader
        headerText="Aftercare recommendations"
        id="aftercare-recommendations-header"
      />
      <ChakraVStack
        alignItems="flex-start"
        aria-labelledby="aftercare-recommendations-header"
        gap="12px"
        width="100%"
      >
        {populatedFields.map((field) => (
          <AftercareSectionCard
            id={field.id}
            isSigned={isSigned}
            key={field.key}
            label={field.label}
            onClickEdit={() => handleSelectField(field)}
            value={dischargePlanData![field.key]!}
          />
        ))}
        {isSigned && populatedFields.length === 0 && (
          <Text color="neutral.tertiary" variant="body">
            No details provided
          </Text>
        )}
        {!isSigned && emptyFields.length > 0 && (
          <ChakraFlex flexWrap="wrap" gap="8px">
            {emptyFields.map((field) => (
              <FireflyButton
                ariaLabel={`add-aftercare-${field.id}`}
                id={`add-aftercare-${field.id}`}
                key={field.key}
                leftIcon={FAIconName.plusSolid}
                onClick={() => handleSelectField(field)}
                size="small"
                variant="secondary"
              >
                {`Add ${field.label.toLowerCase()}`}
              </FireflyButton>
            ))}
          </ChakraFlex>
        )}
      </ChakraVStack>
    </Section>
  );
};

export default AftercareRecommendationsSection;
